// ----------------------------------------------------------------------------------------------------------------------------------
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import HomeService from "../Services/HomeService";
import HttpClientXml from "../utils/HttpClientXml";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

const headLineStyle = {
  textAlign: "center",
  fontSize: "30px",
  color: "#868e96",
  margin: "15px",
  fontWeight: "bold",
};

const AddCityDetails = () => {
  const userCity = localStorage.getItem("city");
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Initial form data structure matching the Postman body
  const [formData, setFormData] = useState({
    city: "",
    firstSection: {
      image: "",
      mainTag: "",
      header: "",
      descriptions: [""],
      tags: [{ icon: "", content: "" }],
    },
    thirdSection: {
      image: "",
      heading: "",
      desc: "",
      points: [{ icon: "", content: "" }],
    },
    fourthSection: {
      heading: "",
      subHeading: "",
      sections: [{ icon: "", header: "", desc: "" }],
    },
    fifthSection: {
      heading: "",
      desc: "",
      sections: [{ icon: "", heading: "", desc: "" }],
    },
    seventhSection: {
      counts: [{ heading: "", subHeading: "" }],
      midHeading: "",
      midSubHeading: "",
      works: [{ icon: "", heading: "", subheading: "" }],
    },
    eighthSection: {
      image: "",
      heading: "",
      description: "",
      appStore: "",
      playStore: "",
    },
    ninethSection: {
      heading: "",
      desc: "",
    },
  });

  // State to track uploaded image URLs for preview
  const [uploadedImages, setUploadedImages] = useState({});

  // Fetch cities
  const fetchCities = async () => {
    try {
      const res = await HomeService.getCities();
      if (res?.data) {
        setCities(res.data);
        if (userCity) {
          setCities([res.data.find((item) => item._id === userCity)]);
        }
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
      toast.error("Failed to fetch cities");
    }
  };

  // Handle city change
  const handleCityChange = (e) => {
    const cityId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      city: cityId,
    }));
  };

  // Improved updateFormData helper that handles nested paths
  const updateFormData = (path, value) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      const pathParts = path.split(".");
      let current = newData;

      // Navigate to the parent object
      for (let i = 0; i < pathParts.length - 1; i++) {
        if (!current[pathParts[i]]) {
          // If path doesn't exist, create it
          current[pathParts[i]] = {};
        }
        current = current[pathParts[i]];
      }

      // Set the value
      current[pathParts[pathParts.length - 1]] = value;
      return newData;
    });
  };

  // Handle array item update (for nested arrays with indexes)
  const updateArrayItem = (index, value, arrayPath, field) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      const pathParts = arrayPath.split(".");
      let current = newData;

      // Navigate to the array
      for (let i = 0; i < pathParts.length; i++) {
        if (!current[pathParts[i]]) {
          current[pathParts[i]] = [];
        }
        current = current[pathParts[i]];
      }

      // Make sure the array item exists
      if (!current[index]) {
        current[index] = {};
      }

      if (field) {
        current[index][field] = value;
      } else {
        current[index] = value;
      }

      return newData;
    });
  };

  // Image upload function
  const handleImageUpload = async (file, path) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file");
      return null;
    }

    const data = new FormData();
    data.append("image", file);

    try {
      const res = await HttpClientXml.fileUplode("image-upload", "POST", data);
      if (res?.image) {
        // Check if path contains array index notation like [0]
        if (path.includes("[") && path.includes("]")) {
          // Extract array path and index
          const match = path.match(/^(.+?)\[(\d+)\]\.(.+)$/);
          if (match) {
            const arrayPath = match[1];
            const index = parseInt(match[2]);
            const field = match[3];
            updateArrayItem(index, res.image, arrayPath, field);
          } else {
            updateFormData(path, res.image);
          }
        } else {
          updateFormData(path, res.image);
        }

        // Store for preview
        setUploadedImages((prev) => ({
          ...prev,
          [path]: res.image,
        }));

        toast.success("Image uploaded successfully!");
        return res.image;
      } else {
        toast.error(res?.message || "Failed to upload image");
        return null;
      }
    } catch (error) {
      toast.error("Failed to upload image.");
      console.error("Error uploading image:", error);
      return null;
    }
  };

  // Handle text input changes
  const handleTextChange = (e, path) => {
    const { value } = e.target;
    updateFormData(path, value);
  };

  // Handle array item changes (for forms with array inputs)
  const handleArrayItemChange = (index, value, arrayPath, field) => {
    updateArrayItem(index, value, arrayPath, field);
  };

  // Add new item to array (single item)
  const addArrayItem = (arrayPath, defaultValue) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      const pathParts = arrayPath.split(".");
      let current = newData;

      for (let i = 0; i < pathParts.length; i++) {
        if (!current[pathParts[i]]) {
          current[pathParts[i]] = [];
        }
        current = current[pathParts[i]];
      }

      // Add only one item
      current.push(defaultValue);
      return newData;
    });
  };

  // Remove item from array
  const removeArrayItem = (index, arrayPath) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      const pathParts = arrayPath.split(".");
      let current = newData;

      for (let i = 0; i < pathParts.length; i++) {
        current = current[pathParts[i]];
      }

      // Simply remove the item if there are more than 1
      if (current.length > 1) {
        current.splice(index, 1);
      } else {
        // If it's the last item, reset it to empty/default value
        if (typeof current[0] === "object") {
          // For objects, reset all properties
          Object.keys(current[0]).forEach((key) => {
            current[0][key] = "";
          });
        } else {
          // For strings/arrays, reset to empty
          current[0] = "";
        }
      }

      return newData;
    });
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.city) {
      toast.error("Please select a city");
      return;
    }

    setLoading(true);
    try {
      console.log("Submitting data:", JSON.stringify(formData, null, 2));
      const res = await HomeService.createCityDetails(formData);

      if (res?.status) {
        toast.success("City details added successfully!");
        navigate("/manage-city-details");
      } else {
        toast.error(res?.message || "Failed to add city details");
      }
    } catch (error) {
      toast.error("Failed to add city details.");
      console.error("Error adding city details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCities();
  }, []);

  return (
    <div className="main_wrap" style={{ marginTop: "6rem" }}>
      <div className="container-fluid">
        <div className="mt-5">
          <div style={headLineStyle} className="page-headline">
            Add City Details
          </div>
          <hr />

          <section className="chartBox_area">
            <form onSubmit={handleSubmit}>
              {/* City Selection */}
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">City Selection</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <label>
                        Select City<span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-control"
                        name="city"
                        value={formData.city}
                        onChange={handleCityChange}
                        required
                      >
                        <option value="" disabled>
                          ---Select City---
                        </option>
                        {cities.map((city) => (
                          <option key={city._id} value={city._id}>
                            {city.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* First Section */}
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">Banner Section</h5>
                </div>
                <div className="card-body">
                  {/* Header Image Upload */}
                  <div className="row">
                    <div className="col-md-12 mt-3">
                      <label>Header Image</label>
                      <div>
                        {uploadedImages["firstSection.image"] && (
                          <div className="mb-3">
                            <img
                              src={uploadedImages["firstSection.image"]}
                              alt="Header Preview"
                              style={{
                                width: "200px",
                                height: "150px",
                                objectFit: "cover",
                                borderRadius: "5px",
                              }}
                            />
                            <div className="small text-success mt-1">
                              <i className="fa-solid fa-check me-1"></i>
                              Image uploaded
                            </div>
                          </div>
                        )}
                        <div className="input-group">
                          <input
                            type="file"
                            className="form-control"
                            accept="image/*"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (file) {
                                await handleImageUpload(
                                  file,
                                  "firstSection.image",
                                );
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6 mt-3">
                      <label>Main Tag</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.firstSection?.mainTag || ""}
                        onChange={(e) =>
                          handleTextChange(e, "firstSection.mainTag")
                        }
                        placeholder="e.g., Premium Properties"
                      />
                    </div>
                    <div className="col-md-6 mt-3">
                      <label>Header</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.firstSection?.header || ""}
                        onChange={(e) =>
                          handleTextChange(e, "firstSection.header")
                        }
                        placeholder="e.g., Discover Your Dream Home"
                      />
                    </div>
                  </div>

                  {/* Descriptions */}
                  <div className="mt-3">
                    <label>Descriptions</label>
                    {(formData.firstSection?.descriptions || []).map(
                      (desc, index) => (
                        <div key={index} className="input-group mb-2">
                          <input
                            type="text"
                            className="form-control"
                            value={desc || ""}
                            onChange={(e) =>
                              handleArrayItemChange(
                                index,
                                e.target.value,
                                "firstSection.descriptions",
                              )
                            }
                            placeholder={`Description ${index + 1}`}
                          />
                          {(formData.firstSection?.descriptions || []).length >
                            1 && (
                            <button
                              type="button"
                              className="btn btn-outline-danger"
                              onClick={() =>
                                removeArrayItem(
                                  index,
                                  "firstSection.descriptions",
                                )
                              }
                            >
                              <i className="fa-solid fa-trash"></i>
                            </button>
                          )}
                        </div>
                      ),
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() =>
                        addArrayItem("firstSection.descriptions", "")
                      }
                    >
                      <i className="fa-solid fa-plus me-1"></i> Add Description
                    </button>
                  </div>

                  {/* Tags */}
                  <div className="mt-3">
                    <label>Tags</label>
                    {(formData.firstSection?.tags || []).map((tag, index) => (
                      <div key={index} className="border p-3 mb-3 rounded">
                        <div className="row">
                          <div className="col-md-6">
                            <label>Icon Image</label>
                            <div className="mb-2">
                              {uploadedImages[
                                `firstSection.tags[${index}].icon`
                              ] && (
                                <div className="mb-2">
                                  <img
                                    src={
                                      uploadedImages[
                                        `firstSection.tags[${index}].icon`
                                      ]
                                    }
                                    alt="Preview"
                                    style={{
                                      width: "50px",
                                      height: "50px",
                                      objectFit: "cover",
                                      borderRadius: "5px",
                                    }}
                                  />
                                  <div className="small text-success mt-1">
                                    <i className="fa-solid fa-check me-1"></i>
                                    Image uploaded
                                  </div>
                                </div>
                              )}
                              <input
                                type="file"
                                className="form-control"
                                accept="image/*"
                                onChange={async (e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    await handleImageUpload(
                                      file,
                                      `firstSection.tags[${index}].icon`,
                                    );
                                  }
                                }}
                              />
                            </div>
                          </div>
                          <div className="col-md-5">
                            <label>Content</label>
                            <input
                              type="text"
                              className="form-control"
                              value={tag?.content || ""}
                              onChange={(e) =>
                                handleArrayItemChange(
                                  index,
                                  e.target.value,
                                  "firstSection.tags",
                                  "content",
                                )
                              }
                              placeholder="Tag content"
                            />
                          </div>
                          <div className="col-md-1 d-flex align-items-end">
                            {(formData.firstSection?.tags || []).length > 1 && (
                              <button
                                type="button"
                                className="btn btn-outline-danger"
                                onClick={() =>
                                  removeArrayItem(index, "firstSection.tags")
                                }
                              >
                                <i className="fa-solid fa-trash"></i>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() =>
                        addArrayItem("firstSection.tags", {
                          icon: "",
                          content: "",
                        })
                      }
                    >
                      <i className="fa-solid fa-plus me-1"></i> Add Tag
                    </button>
                  </div>
                </div>
              </div>

              {/* Fourth Section - Our Services */}
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">Our Services Section</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6 mt-3">
                      <label>Heading</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.fourthSection?.heading || ""}
                        onChange={(e) =>
                          handleTextChange(e, "fourthSection.heading")
                        }
                        placeholder="Services heading"
                      />
                    </div>
                    <div className="col-md-6 mt-3">
                      <label>Sub Heading</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.fourthSection?.subHeading || ""}
                        onChange={(e) =>
                          handleTextChange(e, "fourthSection.subHeading")
                        }
                        placeholder="Services sub heading"
                      />
                    </div>
                  </div>

                  {/* Service Sections */}
                  <div className="mt-3">
                    <label>Service Items</label>
                    {(formData.fourthSection?.sections || []).map(
                      (section, index) => (
                        <div key={index} className="border p-3 mb-3 rounded">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0">Service {index + 1}</h6>
                            {(formData.fourthSection?.sections || []).length >
                              1 && (
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() =>
                                  removeArrayItem(
                                    index,
                                    "fourthSection.sections",
                                  )
                                }
                              >
                                <i className="fa-solid fa-trash"></i>
                              </button>
                            )}
                          </div>
                          <div className="row">
                            <div className="col-md-4">
                              <label>Icon</label>
                              <div>
                                {uploadedImages[
                                  `fourthSection.sections[${index}].icon`
                                ] && (
                                  <div className="mb-2">
                                    <img
                                      src={
                                        uploadedImages[
                                          `fourthSection.sections[${index}].icon`
                                        ]
                                      }
                                      alt="Preview"
                                      style={{
                                        width: "50px",
                                        height: "50px",
                                        objectFit: "cover",
                                        borderRadius: "5px",
                                      }}
                                    />
                                    <div className="small text-success mt-1">
                                      <i className="fa-solid fa-check me-1"></i>
                                      Image uploaded
                                    </div>
                                  </div>
                                )}
                                <input
                                  type="file"
                                  className="form-control"
                                  accept="image/*"
                                  onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      await handleImageUpload(
                                        file,
                                        `fourthSection.sections[${index}].icon`,
                                      );
                                    }
                                  }}
                                />
                              </div>
                            </div>
                            <div className="col-md-4">
                              <label>Header</label>
                              <input
                                type="text"
                                className="form-control"
                                value={section?.header || ""}
                                onChange={(e) =>
                                  handleArrayItemChange(
                                    index,
                                    e.target.value,
                                    "fourthSection.sections",
                                    "header",
                                  )
                                }
                                placeholder="Service header"
                              />
                            </div>
                            <div className="col-md-4">
                              <label>Description</label>
                              <textarea
                                className="form-control"
                                value={section?.desc || ""}
                                onChange={(e) =>
                                  handleArrayItemChange(
                                    index,
                                    e.target.value,
                                    "fourthSection.sections",
                                    "desc",
                                  )
                                }
                                placeholder="Service description"
                                rows="2"
                              />
                            </div>
                          </div>
                        </div>
                      ),
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() =>
                        addArrayItem("fourthSection.sections", {
                          icon: "",
                          header: "",
                          desc: "",
                        })
                      }
                    >
                      <i className="fa-solid fa-plus me-1"></i> Add Service Item
                    </button>
                  </div>
                </div>
              </div>

              {/* Fifth Section - Property Types */}
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">Property Types Section</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6 mt-3">
                      <label>Heading</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.fifthSection?.heading || ""}
                        onChange={(e) =>
                          handleTextChange(e, "fifthSection.heading")
                        }
                        placeholder="Property types heading"
                      />
                    </div>
                    <div className="col-md-6 mt-3">
                      <label>Description</label>
                      <textarea
                        className="form-control"
                        value={formData.fifthSection?.desc || ""}
                        onChange={(e) =>
                          handleTextChange(e, "fifthSection.desc")
                        }
                        placeholder="Property types description"
                        rows="2"
                      />
                    </div>
                  </div>

                  {/* Property Type Sections */}
                  <div className="mt-3">
                    <label>Property Type Items</label>
                    {(formData.fifthSection?.sections || []).map(
                      (section, index) => (
                        <div key={index} className="border p-3 mb-3 rounded">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0">Property Type {index + 1}</h6>
                            {(formData.fifthSection?.sections || []).length >
                              1 && (
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() =>
                                  removeArrayItem(
                                    index,
                                    "fifthSection.sections",
                                  )
                                }
                              >
                                <i className="fa-solid fa-trash"></i>
                              </button>
                            )}
                          </div>
                          <div className="row">
                            <div className="col-md-4">
                              <label>Icon</label>
                              <div>
                                {uploadedImages[
                                  `fifthSection.sections[${index}].icon`
                                ] && (
                                  <div className="mb-2">
                                    <img
                                      src={
                                        uploadedImages[
                                          `fifthSection.sections[${index}].icon`
                                        ]
                                      }
                                      alt="Preview"
                                      style={{
                                        width: "50px",
                                        height: "50px",
                                        objectFit: "cover",
                                        borderRadius: "5px",
                                      }}
                                    />
                                    <div className="small text-success mt-1">
                                      <i className="fa-solid fa-check me-1"></i>
                                      Image uploaded
                                    </div>
                                  </div>
                                )}
                                <input
                                  type="file"
                                  className="form-control"
                                  accept="image/*"
                                  onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      await handleImageUpload(
                                        file,
                                        `fifthSection.sections[${index}].icon`,
                                      );
                                    }
                                  }}
                                />
                              </div>
                            </div>
                            <div className="col-md-4">
                              <label>Heading</label>
                              <input
                                type="text"
                                className="form-control"
                                value={section?.heading || ""}
                                onChange={(e) =>
                                  handleArrayItemChange(
                                    index,
                                    e.target.value,
                                    "fifthSection.sections",
                                    "heading",
                                  )
                                }
                                placeholder="Property type heading"
                              />
                            </div>
                            <div className="col-md-4">
                              <label>Description</label>
                              <textarea
                                className="form-control"
                                value={section?.desc || ""}
                                onChange={(e) =>
                                  handleArrayItemChange(
                                    index,
                                    e.target.value,
                                    "fifthSection.sections",
                                    "desc",
                                  )
                                }
                                placeholder="Property type description"
                                rows="2"
                              />
                            </div>
                          </div>
                        </div>
                      ),
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() =>
                        addArrayItem("fifthSection.sections", {
                          icon: "",
                          heading: "",
                          desc: "",
                        })
                      }
                    >
                      <i className="fa-solid fa-plus me-1"></i> Add Property
                      Type
                    </button>
                  </div>
                </div>
              </div>

              {/* Seventh Section - Statistics & How It Works */}
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">Statistics & How It Works Section</h5>
                </div>
                <div className="card-body">
                  {/* Counts */}
                  <div className="border p-3 mb-3 rounded">
                    <h6>Statistics Counts</h6>
                    {(formData.seventhSection?.counts || []).map(
                      (count, index) => (
                        <div key={index} className="border-bottom pb-2 mb-2">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0">Count {index + 1}</h6>
                            {(formData.seventhSection?.counts || []).length >
                              1 && (
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() =>
                                  removeArrayItem(
                                    index,
                                    "seventhSection.counts",
                                  )
                                }
                              >
                                <i className="fa-solid fa-trash"></i>
                              </button>
                            )}
                          </div>
                          <div className="row">
                            <div className="col-md-6">
                              <label>Heading (Number)</label>
                              <input
                                type="text"
                                className="form-control"
                                value={count?.heading || ""}
                                onChange={(e) =>
                                  handleArrayItemChange(
                                    index,
                                    e.target.value,
                                    "seventhSection.counts",
                                    "heading",
                                  )
                                }
                                placeholder="e.g., 10000+"
                              />
                            </div>
                            <div className="col-md-6">
                              <label>Sub Heading</label>
                              <input
                                type="text"
                                className="form-control"
                                value={count?.subHeading || ""}
                                onChange={(e) =>
                                  handleArrayItemChange(
                                    index,
                                    e.target.value,
                                    "seventhSection.counts",
                                    "subHeading",
                                  )
                                }
                                placeholder="e.g., Properties"
                              />
                            </div>
                          </div>
                        </div>
                      ),
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary mt-2"
                      onClick={() =>
                        addArrayItem("seventhSection.counts", {
                          heading: "",
                          subHeading: "",
                        })
                      }
                    >
                      <i className="fa-solid fa-plus me-1"></i> Add Statistic
                      Count
                    </button>
                  </div>

                  {/* How It Works Heading */}
                  <div className="row mt-3">
                    <div className="col-md-6">
                      <label>How It Works Heading</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.seventhSection?.midHeading || ""}
                        onChange={(e) =>
                          handleTextChange(e, "seventhSection.midHeading")
                        }
                        placeholder="How it works heading"
                      />
                    </div>
                    <div className="col-md-6">
                      <label>How It Works Sub Heading</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.seventhSection?.midSubHeading || ""}
                        onChange={(e) =>
                          handleTextChange(e, "seventhSection.midSubHeading")
                        }
                        placeholder="How it works sub heading"
                      />
                    </div>
                  </div>

                  {/* Works Steps */}
                  <div className="mt-3">
                    <label>How It Works Steps</label>
                    {(formData.seventhSection?.works || []).map(
                      (work, index) => (
                        <div key={index} className="border p-3 mb-3 rounded">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0">Step {index + 1}</h6>
                            {(formData.seventhSection?.works || []).length >
                              1 && (
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() =>
                                  removeArrayItem(index, "seventhSection.works")
                                }
                              >
                                <i className="fa-solid fa-trash"></i>
                              </button>
                            )}
                          </div>
                          <div className="row">
                            <div className="col-md-4">
                              <label>Icon</label>
                              <div>
                                {uploadedImages[
                                  `seventhSection.works[${index}].icon`
                                ] && (
                                  <div className="mb-2">
                                    <img
                                      src={
                                        uploadedImages[
                                          `seventhSection.works[${index}].icon`
                                        ]
                                      }
                                      alt="Preview"
                                      style={{
                                        width: "50px",
                                        height: "50px",
                                        objectFit: "cover",
                                        borderRadius: "5px",
                                      }}
                                    />
                                    <div className="small text-success mt-1">
                                      <i className="fa-solid fa-check me-1"></i>
                                      Image uploaded
                                    </div>
                                  </div>
                                )}
                                <input
                                  type="file"
                                  className="form-control"
                                  accept="image/*"
                                  onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      await handleImageUpload(
                                        file,
                                        `seventhSection.works[${index}].icon`,
                                      );
                                    }
                                  }}
                                />
                              </div>
                            </div>
                            <div className="col-md-4">
                              <label>Heading</label>
                              <input
                                type="text"
                                className="form-control"
                                value={work?.heading || ""}
                                onChange={(e) =>
                                  handleArrayItemChange(
                                    index,
                                    e.target.value,
                                    "seventhSection.works",
                                    "heading",
                                  )
                                }
                                placeholder="Step heading"
                              />
                            </div>
                            <div className="col-md-4">
                              <label>Subheading</label>
                              <input
                                type="text"
                                className="form-control"
                                value={work?.subheading || ""}
                                onChange={(e) =>
                                  handleArrayItemChange(
                                    index,
                                    e.target.value,
                                    "seventhSection.works",
                                    "subheading",
                                  )
                                }
                                placeholder="Step subheading"
                              />
                            </div>
                          </div>
                        </div>
                      ),
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() =>
                        addArrayItem("seventhSection.works", {
                          icon: "",
                          heading: "",
                          subheading: "",
                        })
                      }
                    >
                      <i className="fa-solid fa-plus me-1"></i> Add Step
                    </button>
                  </div>
                </div>
              </div>

              {/* Third Section - About Section */}
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">About Section</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6 mt-3">
                      <label>Image</label>
                      <div>
                        {uploadedImages["thirdSection.image"] && (
                          <div className="mb-2">
                            <img
                              src={uploadedImages["thirdSection.image"]}
                              alt="Preview"
                              style={{
                                width: "100px",
                                height: "100px",
                                objectFit: "cover",
                                borderRadius: "5px",
                              }}
                            />
                            <div className="small text-success mt-1">
                              <i className="fa-solid fa-check me-1"></i>
                              Image uploaded
                            </div>
                          </div>
                        )}
                        <input
                          type="file"
                          className="form-control"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              await handleImageUpload(
                                file,
                                "thirdSection.image",
                              );
                            }
                          }}
                        />
                      </div>
                    </div>
                    <div className="col-md-6 mt-3">
                      <label>Heading</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.thirdSection?.heading || ""}
                        onChange={(e) =>
                          handleTextChange(e, "thirdSection.heading")
                        }
                        placeholder="About heading"
                      />
                    </div>
                    <div className="col-12 mt-3">
                      <label>Description</label>
                      <textarea
                        className="form-control"
                        value={formData.thirdSection?.desc || ""}
                        onChange={(e) =>
                          handleTextChange(e, "thirdSection.desc")
                        }
                        placeholder="About description"
                        rows="3"
                      />
                    </div>
                  </div>

                  <div className="mt-3">
                    <label>Key Points</label>
                    {(formData.thirdSection?.points || []).map(
                      (point, index) => (
                        <div key={index} className="border p-3 mb-3 rounded">
                          <div className="row">
                            <div className="col-md-6">
                              <label>Icon</label>
                              <div>
                                {uploadedImages[
                                  `thirdSection.points[${index}].icon`
                                ] && (
                                  <div className="mb-2">
                                    <img
                                      src={
                                        uploadedImages[
                                          `thirdSection.points[${index}].icon`
                                        ]
                                      }
                                      alt="Preview"
                                      style={{
                                        width: "50px",
                                        height: "50px",
                                        objectFit: "cover",
                                        borderRadius: "5px",
                                      }}
                                    />
                                    <div className="small text-success mt-1">
                                      <i className="fa-solid fa-check me-1"></i>
                                      Image uploaded
                                    </div>
                                  </div>
                                )}
                                <input
                                  type="file"
                                  className="form-control"
                                  accept="image/*"
                                  onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      await handleImageUpload(
                                        file,
                                        `thirdSection.points[${index}].icon`,
                                      );
                                    }
                                  }}
                                />
                              </div>
                            </div>
                            <div className="col-md-5">
                              <label>Content</label>
                              <input
                                type="text"
                                className="form-control"
                                value={point?.content || ""}
                                onChange={(e) =>
                                  handleArrayItemChange(
                                    index,
                                    e.target.value,
                                    "thirdSection.points",
                                    "content",
                                  )
                                }
                                placeholder="Point content"
                              />
                            </div>
                            <div className="col-md-1 d-flex align-items-end">
                              {(formData.thirdSection?.points || []).length >
                                1 && (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger"
                                  onClick={() =>
                                    removeArrayItem(
                                      index,
                                      "thirdSection.points",
                                    )
                                  }
                                >
                                  <i className="fa-solid fa-trash"></i>
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ),
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() =>
                        addArrayItem("thirdSection.points", {
                          icon: "",
                          content: "",
                        })
                      }
                    >
                      <i className="fa-solid fa-plus me-1"></i> Add Point
                    </button>
                  </div>
                </div>
              </div>

              {/* Eighth Section - App Download */}
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">App Download Section</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6 mt-3">
                      <label>Image</label>
                      <div>
                        {uploadedImages["eighthSection.image"] && (
                          <div className="mb-2">
                            <img
                              src={uploadedImages["eighthSection.image"]}
                              alt="Preview"
                              style={{
                                width: "100px",
                                height: "100px",
                                objectFit: "cover",
                                borderRadius: "5px",
                              }}
                            />
                            <div className="small text-success mt-1">
                              <i className="fa-solid fa-check me-1"></i>
                              Image uploaded
                            </div>
                          </div>
                        )}
                        <input
                          type="file"
                          className="form-control"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              await handleImageUpload(
                                file,
                                "eighthSection.image",
                              );
                            }
                          }}
                        />
                      </div>
                    </div>
                    <div className="col-md-6 mt-3">
                      <label>Heading</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.eighthSection?.heading || ""}
                        onChange={(e) =>
                          handleTextChange(e, "eighthSection.heading")
                        }
                        placeholder="App download heading"
                      />
                    </div>
                    <div className="col-12 mt-3">
                      <label>Description</label>
                      <textarea
                        className="form-control"
                        value={formData.eighthSection?.description || ""}
                        onChange={(e) =>
                          handleTextChange(e, "eighthSection.description")
                        }
                        placeholder="App download description"
                        rows="2"
                      />
                    </div>
                    <div className="col-md-6 mt-3">
                      <label>App Store URL</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.eighthSection?.appStore || ""}
                        onChange={(e) =>
                          handleTextChange(e, "eighthSection.appStore")
                        }
                        placeholder="iOS App Store URL"
                      />
                    </div>
                    <div className="col-md-6 mt-3">
                      <label>Play Store URL</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.eighthSection?.playStore || ""}
                        onChange={(e) =>
                          handleTextChange(e, "eighthSection.playStore")
                        }
                        placeholder="Google Play Store URL"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Ninth Section - Contact */}
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">Contact Section</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6 mt-3">
                      <label>Heading</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.ninethSection?.heading || ""}
                        onChange={(e) =>
                          handleTextChange(e, "ninethSection.heading")
                        }
                        placeholder="Contact section heading"
                      />
                    </div>
                    <div className="col-md-6 mt-3">
                      <label>Description</label>
                      <textarea
                        className="form-control"
                        value={formData.ninethSection?.desc || ""}
                        onChange={(e) =>
                          handleTextChange(e, "ninethSection.desc")
                        }
                        placeholder="Contact section description"
                        rows="2"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="text-center mt-4">
                <button
                  type="submit"
                  className="btn btn-primary btn-lg"
                  style={{
                    background: "rgb(133, 218, 79)",
                    border: "none",
                    padding: "12px 40px",
                    fontSize: "18px",
                    fontWeight: "600",
                  }}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin me-2"></i>
                      Submitting...
                    </>
                  ) : (
                    "Submit City Details"
                  )}
                </button>
              </div>
            </form>
          </section>
        </div>
      </div>

      {/* CSS for react-select */}
      <style>
        {`
          .basic-multi-select .select__control {
            border: 1px solid #ced4da;
            min-height: 38px;
          }
          .basic-multi-select .select__control--is-focused {
            border-color: #80bdff;
            box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
          }
        `}
      </style>
    </div>
  );
};

export default AddCityDetails;
